import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Seo from '@/components/seo/Seo';
import { getRelatedDictionary, getRelatedSite } from '@/data/related';
import type { RelatedBlock, RelatedImage } from '@/data/related/types';

/**
 * 関連事業 詳細ページ(/related/:slug)の共通描画 — 2026-09-18 作り直し。
 * 章立て・分量は THE BUTLER の関連サイト詳細(blocks 10・FAQ 6)と同等。
 * 見た目はアカデミーの既存部品のまま(hero-billboard / gold-line / card-glow / bg-primary-900 帯 / FAQ アコーディオン)。
 * 本文は src/data/related/{ja,en}.ts(辞書方式)。事実は紹介先の正典から書き直したもののみ。
 */

function FadeInSection({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

function SectionHead({ label, title, sub, dark = false }: { label: string; title: string; sub: string; dark?: boolean }) {
  return (
    <div className="text-center mb-12">
      <p className={`${dark ? 'text-accent-400' : 'text-accent-600'} text-[13px] tracking-[0.2em] mb-5 font-label`}>{label}</p>
      <h2 className={`font-heading text-xl md:text-2xl ${dark ? 'text-foreground-50' : 'text-foreground-900'} tracking-[0.06em] mb-2`}>{title}</h2>
      <p className={`section-title-en ${dark ? 'text-accent-400' : 'text-accent-500'} text-[15px] tracking-[0.12em] mb-4`}>{sub}</p>
      <div className="gold-line mx-auto"></div>
    </div>
  );
}

/**
 * 長い段落を文の切れ目で割って、文字の壁にしない(可読性基準: 1 段落 250 字以下)。
 * 日本語は「。」、英語は . ! ? を区切りに使い、上限を超えた分だけ次の段落へ送る。
 * データ側の本文は書き換えず、表示のときにだけ分ける。
 */
export function paragraphs(text: string | string[], limit = 250): string[] {
  const src = Array.isArray(text) ? text : [text];
  const out: string[] = [];
  for (const t of src) {
    if (t.length <= limit) {
      out.push(t);
      continue;
    }
    const sentences = t.match(/[^。.!?]*(?:[。.!?]+(?:["')\]」』]|\s)?|$)/g)?.filter((s) => s.trim()) ?? [t];
    let buf = '';
    for (const s of sentences) {
      if (buf && (buf + s).length > limit) {
        out.push(buf.trim());
        buf = s;
      } else {
        buf += s;
      }
    }
    if (buf.trim()) out.push(buf.trim());
  }
  return out;
}

function Img({ img, className }: { img: RelatedImage; className: string }) {
  return (
    <img
      src={img.src}
      alt={img.alt}
      loading="lazy"
      className={className}
      style={img.position ? { objectPosition: img.position } : undefined}
    />
  );
}

export default function RelatedSiteDetail({ slug }: { slug: string }) {
  const { lang } = useLanguageContext();
  const dict = getRelatedDictionary(lang);
  const site = getRelatedSite(lang, slug);
  const { common } = dict;
  const href = (path: string) => (lang === 'en' ? (path === '/' ? '/en' : `/en${path}`) : path);

  if (!site) return null;

  const others = dict.sites.filter((s) => s.slug !== slug);
  const route = `/related/${slug}`;

  const webPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: site.heroTitle,
    description: site.heroLead,
    url: `https://butler-academy.jp${route}`,
    isPartOf: {
      '@type': 'WebSite',
      name: lang === 'en' ? 'Japan Butler Academy' : '日本執事アカデミー',
      url: 'https://butler-academy.jp/',
    },
    about: {
      '@type': 'Organization',
      name: site.name,
      url: site.officialUrl,
    },
  };

  const renderBlock = (b: RelatedBlock, i: number) => {
    if (b.kind === 'twocol') {
      return (
        <FadeInSection key={i}>
          <section className={`relative py-20 md:py-28 ${b.tint ? 'bg-background-100/50' : 'bg-background-50'}`}>
            <div className="max-w-[1100px] mx-auto px-8">
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center ${b.reverse ? 'md:[&>*:first-child]:order-2' : ''}`}>
                <div>
                  <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{b.label}</p>
                  <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">{b.title}</h2>
                  <p className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{b.sub}</p>
                  <div className="gold-line-left mb-8"></div>
                  <div className="space-y-5 text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose">
                    {paragraphs(b.body).map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                  </div>
                  {b.subs && (
                    <div className="mt-8 space-y-5">
                      {b.subs.map((s, j) => (
                        <div key={j} className="border-l-2 border-accent-500/50 pl-5">
                          <h3 className="font-heading text-[16px] text-foreground-900 tracking-[0.05em] mb-2">{s.t}</h3>
                          {paragraphs(s.b).map((t, k) => (
                            <p key={k} className="text-[14px] md:text-[15px] text-foreground-500 tracking-[0.03em] leading-relaxed mb-3 last:mb-0">{t}</p>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="relative h-[300px] md:h-[460px] overflow-hidden">
                  <Img img={b.image} className="absolute inset-0 w-full h-full object-cover" />
                </div>
              </div>
            </div>
          </section>
        </FadeInSection>
      );
    }

    if (b.kind === 'cards') {
      const hasImages = b.cards.some((c) => c.image);
      return (
        <FadeInSection key={i}>
          <section className={`relative py-20 md:py-28 ${b.tint ? 'bg-background-100/50' : 'bg-background-50'}`}>
            <div className="max-w-[1200px] mx-auto px-8">
              <SectionHead label={b.label} title={b.title} sub={b.sub} />
              <div className="max-w-[860px] mx-auto mb-12 space-y-4">{paragraphs(b.lede, 250).map((t, k) => (<p key={k} className="text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose">{t}</p>))}</div>
              {/* 3の倍数は3列、それ以外(4枚等)は2列(最終行に1枚だけ残さない) */}
              <div className={`grid grid-cols-1 sm:grid-cols-2 ${b.cards.length % 3 === 0 && hasImages ? 'lg:grid-cols-3' : ''} gap-6`}>
                {b.cards.map((c, j) => (
                  <div key={j} className="card-glow group flex flex-col bg-background-50 border border-background-200/60 overflow-hidden">
                    {c.image && (
                      <div className="relative h-[200px] overflow-hidden">
                        <Img img={c.image} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      </div>
                    )}
                    <div className="flex flex-col flex-1 p-7">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full border border-accent-500/40 text-accent-600 mb-5 font-heading text-[15px]">
                        {String(j + 1).padStart(2, '0')}
                      </div>
                      <h3 className="font-heading text-[16px] text-foreground-900 tracking-[0.05em] mb-3">{c.title}</h3>
                      {paragraphs(c.desc, 250).map((t, k) => (<p key={k} className="text-[14px] text-foreground-500 tracking-[0.03em] leading-relaxed mb-2 last:mb-0">{t}</p>))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </FadeInSection>
      );
    }

    if (b.kind === 'steps') {
      return (
        <FadeInSection key={i}>
          <section className="relative py-20 md:py-28 bg-background-100/50">
            <div className={`mx-auto px-8 ${b.image ? 'max-w-[1200px]' : 'max-w-[900px]'}`}>
              <SectionHead label={b.label} title={b.title} sub={b.sub} />
              <div className="mb-12 max-w-[900px] mx-auto space-y-4">{paragraphs(b.lede, 250).map((t, k) => (<p key={k} className="text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose">{t}</p>))}</div>
              <div className={b.image ? 'grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10 items-start' : ''}>
              <ol className="space-y-6">
                {b.steps.map((s, j) => (
                  <li key={j} className="flex gap-6 bg-background-50 border border-background-200/60 p-6 md:p-7">
                    <span className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full bg-primary-900 text-accent-400 font-heading text-[16px] tracking-[0.05em]">
                      {String(j + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <h3 className="font-heading text-[16px] md:text-[17px] text-foreground-900 tracking-[0.05em] mb-2">{s.title}</h3>
                      {paragraphs(s.desc, 250).map((t, k) => (<p key={k} className="text-[14px] md:text-[15px] text-foreground-500 tracking-[0.03em] leading-relaxed mb-2 last:mb-0">{t}</p>))}
                    </div>
                  </li>
                ))}
              </ol>
              {b.image && (
                <figure className="m-0 lg:sticky lg:top-28">
                  <div className="relative h-[300px] lg:h-[520px] overflow-hidden">
                    <Img img={b.image} className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                  <figcaption className="mt-2 text-[12px] text-foreground-400 tracking-[0.04em] leading-relaxed">{b.image.alt}</figcaption>
                </figure>
              )}
              </div>
            </div>
          </section>
        </FadeInSection>
      );
    }

    if (b.kind === 'band') {
      return (
        <FadeInSection key={i}>
          <section className="relative py-20 md:py-28 bg-primary-900 text-foreground-200">
            <div className="max-w-[820px] mx-auto px-8">
              <SectionHead label={b.label} title={b.title} sub={b.sub} dark />
              <div className="space-y-6 text-[15px] md:text-[16px] text-foreground-200/80 tracking-[0.03em] leading-loose">
                {paragraphs(b.body).map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
              </div>
              {b.subs && (
                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
                  {b.subs.map((s, j) => (
                    <div key={j} className="border-l-2 border-accent-400/50 pl-5">
                      <h3 className="font-heading text-[16px] text-foreground-50 tracking-[0.05em] mb-2">{s.t}</h3>
                      {paragraphs(s.b).map((t, k) => (
                        <p key={k} className="text-[14px] md:text-[15px] text-foreground-200/75 tracking-[0.03em] leading-relaxed mb-3 last:mb-0">{t}</p>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {b.image && (
              <div className="max-w-[1100px] mx-auto px-8 mt-14">
                <div className="relative h-[260px] md:h-[420px] overflow-hidden">
                  <Img img={b.image} className="absolute inset-0 w-full h-full object-cover" />
                </div>
                <p className="mt-2 text-[12px] text-foreground-400 tracking-[0.04em]">{b.image.alt}</p>
              </div>
            )}
          </section>
        </FadeInSection>
      );
    }

    if (b.kind === 'gallery') {
      return (
        <FadeInSection key={i}>
          <section className="relative py-16 md:py-20 bg-background-50">
            <div className="max-w-[1200px] mx-auto px-8">
              <div className={`grid grid-cols-1 sm:grid-cols-2 ${b.images.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4`}>
                {b.images.map((img, j) => (
                  <figure key={j} className="m-0">
                    <div className="relative h-[220px] overflow-hidden">
                      <Img img={img} className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                    <figcaption className="mt-2 text-[12px] text-foreground-400 tracking-[0.04em] leading-relaxed">{img.alt}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </section>
        </FadeInSection>
      );
    }

    // official
    return (
      <FadeInSection key={i}>
        <section className="relative py-20 md:py-24 bg-background-100/50">
          <div className="max-w-[720px] mx-auto px-8 text-center">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-4">{b.title}</h2>
            <div className="mb-8 space-y-3">{paragraphs(b.body, 250).map((t, k) => (<p key={k} className="text-[15px] text-foreground-600 tracking-[0.03em] leading-relaxed">{t}</p>))}</div>
            <a
              href={b.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-400 text-primary-900 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
            >
              <span>{common.official}</span>
              <i className="ri-external-link-line text-sm"></i>
            </a>
            <p className="mt-4 text-[13px] text-foreground-400 tracking-[0.04em]">
              {b.urlLabel} — {common.externalNote}
            </p>
          </div>
        </section>
      </FadeInSection>
    );
  };

  return (
    <>
      <Header />
      <main>
        <Seo
          route={route}
          jsonLd={[webPageStructuredData]}
          faqItems={site.faq.map((f) => ({ question: f.q, answer: f.a }))}
        />

        {/* Hero(アカデミー共通の hero-billboard) */}
        <section className="hero-billboard relative w-full h-[64vh] min-h-[540px] md:h-[82vh] md:min-h-[640px] overflow-hidden">
          <img
            src={site.heroImage.src}
            alt={site.heroImage.alt}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: site.heroImage.position ?? 'center top' }}
          />
          <div className="absolute inset-0 hero-overlay"></div>
          {/* SP は固定ヘッダー分(約 80px)を上に空け、見出しがヘッダーの下に隠れないようにする */}
          <div className="absolute inset-0 flex items-center pt-24 md:pt-0">
            <div className="relative w-full px-8">
              <div className="max-w-[800px] mx-auto text-center">
                {/* SP はヒーロー直下の共通パンくずに任せ、ヒーロー内の行は md 以上のみ表示(長い名称の折返し回避) */}
                <div className="hidden md:flex items-center justify-center gap-2 mb-8 text-[14px] tracking-[0.05em]">
                  <Link to={href('/')} className="text-foreground-200/70 hover:text-accent-400 transition-colors">
                    {common.home}
                  </Link>
                  <i className="ri-arrow-right-s-line text-foreground-400/60 text-[12px]"></i>
                  <Link to={href('/related')} className="text-foreground-200/70 hover:text-accent-400 transition-colors">
                    {common.parent}
                  </Link>
                  <i className="ri-arrow-right-s-line text-foreground-400/60 text-[12px]"></i>
                  <span className="text-accent-400">{site.name}</span>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-50 tracking-[0.08em] mb-4">{site.heroTitle}</h1>
                <p className="section-title-en text-accent-400 text-[15px] md:text-[17px] tracking-[0.12em] mb-6">{site.heroTitleEn}</p>
                <div className="space-y-3 max-w-[620px] mx-auto">{paragraphs(site.heroLead, 250).map((t, k) => (<p key={k} className="text-[14px] md:text-[16px] text-foreground-200/80 tracking-[0.05em] leading-relaxed max-md:line-clamp-4">{t}</p>))}</div>
                <div className="mt-6 md:mt-8">
                  <a
                    href={site.officialUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border border-accent-400/70 hover:bg-accent-500 hover:text-primary-900 text-accent-400 text-[14px] tracking-[0.05em] px-7 py-3 transition-colors"
                  >
                    <span>{site.officialLabel}</span>
                    <i className="ri-external-link-line text-sm"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
        <Breadcrumb items={[{ label: '関連事業', labelEn: 'Related Businesses', to: '/related' }, { label: site.name, labelEn: site.name }]} />

        {site.blocks.map((b, i) => renderBlock(b, i))}

        {/* FAQ(アカデミー共通のアコーディオン) */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-50">
            <div className="max-w-[860px] mx-auto px-8">
              <SectionHead label={common.faqLabel} title={common.faqTitle} sub={common.faqSub} />
              <Faq items={site.faq} />
            </div>
          </section>
        </FadeInSection>

        {/* 他の関連事業 */}
        <FadeInSection>
          <section className="relative py-16 md:py-20 bg-background-100/50">
            <div className="max-w-[1100px] mx-auto px-8">
              <div className="text-center mb-10">
                <h2 className="font-heading text-lg md:text-xl text-foreground-900 tracking-[0.06em] mb-2">{common.otherTitle}</h2>
                <p className="section-title-en text-accent-500 text-[14px] tracking-[0.12em] mb-4">{common.otherSub}</p>
                <div className="gold-line mx-auto"></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {others.map((o) => (
                  <Link key={o.slug} to={href(`/related/${o.slug}`)} className="card-glow group flex gap-4 bg-background-50 border border-background-200/60 p-4">
                    <div className="relative w-[88px] h-[88px] flex-shrink-0 overflow-hidden">
                      <Img img={o.hubImage} className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-heading text-[15px] text-foreground-900 tracking-[0.05em] mb-1 group-hover:text-accent-600 transition-colors">{o.name}</h3>
                      <p className="text-[13px] text-foreground-500 tracking-[0.03em] leading-relaxed">{o.role}</p>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="text-center pt-8">
                <Link to={href('/related')} className="text-[15px] text-accent-600 hover:text-accent-500 tracking-[0.05em] transition-colors">
                  {common.backToHub}
                </Link>
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* CTA(アカデミーの動線) */}
        <FadeInSection>
          <section className="relative py-20 md:py-24 bg-background-50">
            <div className="max-w-[760px] mx-auto px-8 text-center">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-4">{common.ctaTitle}</h2>
              <p className="text-[15px] text-foreground-600 tracking-[0.03em] leading-relaxed mb-8">{common.ctaBody}</p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link to={href('/courses')} className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-400 text-primary-900 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors">
                  {common.ctaCourses}
                </Link>
                <Link to={href('/contact')} className="inline-flex items-center gap-2 border border-accent-500/50 hover:border-accent-400 text-accent-600 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors">
                  {common.ctaContact}
                </Link>
              </div>
            </div>
          </section>
        </FadeInSection>

        <div className="bg-background-100 py-8">
          <p className="text-center text-[13px] text-foreground-400 tracking-[0.05em]">{site.lastUpdated}</p>
        </div>

        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-11 h-11 rounded-full border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 flex items-center justify-center cursor-pointer z-40"
          aria-label={common.backToTop}
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      </main>
      <Footer />
    </>
  );
}

/** FAQ アコーディオン(faq/page.tsx と同じ文法・faq-question/faq-answer は speakable 用) */
export function Faq({ items }: { items: { q: string; a: string }[] }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="space-y-3">
      {items.map((f, i) => (
        <div key={i} className="bg-background-50 border border-background-200/60">
          <h3 className="m-0">
          <button
            type="button"
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between text-left px-6 py-5 cursor-pointer"
            aria-expanded={open === i}
          >
            <span className="faq-question font-heading text-[16px] md:text-[17px] text-foreground-900 tracking-[0.05em] pr-4">{f.q}</span>
            <span className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className={`text-sm text-accent-600 transition-transform duration-300 ${open === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
            </span>
          </button>
          </h3>
          <div className={`overflow-hidden transition-all duration-300 ${open === i ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="px-6 pb-5 pt-0">
              <div className="border-t border-background-200/40 pt-4">
                {paragraphs(f.a, 250).map((t, k) => (<p key={k} className="faq-answer text-[15px] md:text-[16px] text-foreground-700 leading-relaxed tracking-[0.03em] mb-3 last:mb-0">{t}</p>))}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
